import { useNavigate } from 'react-router-dom';
import { useToastError } from '~/utils/useToastError';
import { useFormik } from 'formik';
import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { InputBar, Button, QuerySelect } from '~/ui';
import styles from './QuestionnaireForm.module.scss';
import cn from 'classnames';
import QuestionsList from './QuestionsList';
import { instance } from '~/utils/api/api';
import { useEffect, useState } from 'react';
import { Modal } from '../Shared/Modal/Modal';
import { getQuestionPayload } from '~/pages/EditQuestionnarie/EditQuestionnaireForm/utils';
import StatIcon from '~/assets/svg/questionnaries/stat-icon.svg?react';
import CreateQuestionForm, {
  QuestionItem,
} from './CreateQuestionForm/CreateQuestionForm';
import EditQuestionForm, {
  QuestionValues,
} from '../EditQuestionForm/EditQuestionForm';
import { useTranslation } from 'react-i18next';
import { useToastSuccess } from '~/utils/useToastSuccess';
import CustomQuerySelect from '../Shared/CustomQuerySelect/CustomQuerySelect';
import CustomSelect from '../Shared/CustomSelect/CustomSelect';
import CustomCheckbox from '../Shared/CustomCheckbox/CustomCheckbox';
import { getUsers } from '~/utils/api/queries';
import { PaginatedResponse } from '~/utils/types/common';
import { UserItem } from '../UserForm';
import { useDebounce } from '@uidotdev/usehooks';
import { validate } from '~/utils/validateQuestionnaires';

interface ITypeOption {
  id: number;
  name: string;
}

export interface IVariant {
  id: number;
  name: string;
  question_close_id?: number;
}

export interface QuestionnairePayload {
  name: string;
  for_novice: boolean;
  publication_date: Dayjs | null;
  removal_date: Dayjs | null;
  type: {
    id: number;
    name: string;
  };
  licensor: string;
  questionnaire_accommodations: { id: number; name: string }[];
  questionnaire_organizations: { id: number; name: string }[];
  accommodation_all: boolean;
  organization_all: boolean;
  user_id: { id: number; name: string }[];
  show_name: boolean;
  show_phone: boolean;
}

const QuestionnaireForm = () => {
  const toastError = useToastError();
  const [typeOptions, setTypeOptions] = useState<ITypeOption[] | []>([]);
  const [questions, setQuestions] = useState<QuestionItem[]>([]);
  const [isAddQuestionModalOpen, setIsAddQuestionModalOpen] = useState(false);
  const [isEditQuestionModalOpen, setIsEditQuestionModalOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[] | []>([]);
  const [editingQuestion, setEditingQuestion] = useState<QuestionItem | null>(
    null
  );
  const [searchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const toastSuccess = useToastSuccess();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const params = {
    sort: '',
    perPage: 100,
    field: '',
    query: debouncedSearchTerm,
    page: 1,
  };

  const initialValues = {
    name: '',
    type: {
      id: 0,
      name: '',
    },
    publication_date: null,
    removal_date: null,
    licensor: '',
    group_send: '1',
    for_novice: false,
    questionnaire_accommodations: [],
    questionnaire_organizations: [],
    accommodation_all: false,
    organization_all: false,
    user_id: [],
    show_name: false,
    show_phone: false,
  };

  const {
    values,
    errors,
    touched,
    isValid,
    isSubmitting,
    resetForm,
    handleBlur,
    setFieldTouched,
    setFieldValue,
    handleSubmit,
    handleChange,
  } = useFormik({
    initialValues,
    validate,
    onSubmit: () => {
      handleFormSubmit(values);
    },
  });

  const handleOpenAddQuestionModal = () => {
    setIsAddQuestionModalOpen(true);
  };

  const handleCloseAddQuestionModal = () => {
    setIsAddQuestionModalOpen(false);
  };

  const handleOpenEditQuestionModal = (id: number) => {
    setEditingQuestion(questions.find(question => question.id === id) || null);
    setIsEditQuestionModalOpen(true);
  };

  const handleCloseEditQuestionModal = () => {
    setIsEditQuestionModalOpen(false);
  };

  const handleDeleteQuestion = async (id: number) => {
    const filteredQuestions = questions.filter(question => question.id !== id);
    setQuestions(filteredQuestions);
  };

  const getUserById = async (userID: number): Promise<UserItem> => {
    const response = await instance.get(`admin/users/${userID}`);
    return response.data.users.data;
  };

  const handleCreateQuestion = async (values: QuestionValues) => {
    const payload = getQuestionPayload(values) as unknown as QuestionItem;

    if (!payload) return;
    const newQuestion = {
      ...payload,
      id: questions.length,
    };
    setQuestions([...questions, newQuestion]);
    handleCloseAddQuestionModal();
  };

  const loadTypeOptions = async () => {
    try {
      const response = await instance.get('admin/questionnaires-type');
      setTypeOptions(response.data);
    } catch (error) {
      toastError(error);
    }
  };

  const handleFormSubmit = async (values: QuestionnairePayload) => {
    try {
      const {
        name,
        publication_date,
        removal_date,
        type,
        licensor,
        questionnaire_accommodations,
        questionnaire_organizations,
        for_novice,
        accommodation_all,
        organization_all,
        show_name,
        show_phone,
      } = values;

      const accommodation_ids = questionnaire_accommodations.map(
        accommodation => accommodation.id
      );
      const organization_ids = questionnaire_organizations.map(
        organization => organization.id
      );

      const payload = {
        name,
        for_novice,
        name_is_required: show_name,
        phone_is_required: show_phone,
        type_id: type.id,
        publication_date: dayjs(publication_date)
          .startOf('day')
          .format('YYYY-MM-DD HH:mm:ss'),
        removal_date: dayjs(removal_date)
          .endOf('day')
          .format('YYYY-MM-DD HH:mm:ss'),
        licensor: licensor,
        ...(accommodation_all ? {} : { accommodation_ids }),
        ...(organization_all ? {} : { organization_ids }),
        ...(accommodation_all ? { accommodation_all } : {}),
        ...(organization_all ? { organization_all } : {}),
        user_ids: selectedUsers,
      };

      const response = await instance.post('admin/questionnaires', payload);
      const questionsWithquestionnaireId = questions.map(question => ({
        ...question,
        question_data: {
          ...question.question_data,
          variants: question.question_data.question_close_variants,
        },
        sequence_number: Number(question.sequence_number),
        questionnaire_id: response.data.id,
      }));
      await instance.post('admin/questions', questionsWithquestionnaireId);

      toastSuccess(t('questionnaire_created'));
      resetForm();
      navigate('/questionnaires');
    } catch (error) {
      toastError(error);
    }
  };

  const handleUpdateQuestion = (values: any) => {
    const payload: QuestionItem = getQuestionPayload(
      values
    ) as unknown as QuestionItem;
    if (!payload) return;

    const updatedQuestions = questions.map(question => {
      if (question.id === editingQuestion?.id) {
        return {
          ...question,
          ...payload,
        };
      }
      return question;
    });

    setQuestions(updatedQuestions);
    handleCloseEditQuestionModal();
  };

  useEffect(() => {
    loadTypeOptions();
  }, []);

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <div className={styles.formInputs}>
        <div className={styles.group}>
          <InputBar
            name="name"
            label={t('anquette_name')}
            placeholder={t('enter_anquette_name')}
            value={values.name}
            disabled={false}
            star={true}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.name && Boolean(errors.name)}
            errors={t(errors.name || '')}
          />
          <InputBar
            name="licensor"
            label={t('licensor_name')}
            placeholder={t('enter_licensor_name')}
            value={values.licensor}
            disabled={false}
            star={true}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.licensor && Boolean(errors.licensor)}
            errors={t(errors.licensor || '')}
          />
        </div>
        <div className={styles.group}>
          <div className={styles.group__item}>
            <CustomSelect
              label={t('questionnaire_subject')}
              placeholder={t('validate_subject')}
              value={values.type}
              onChange={value => setFieldValue('type', value)}
              options={typeOptions}
              required
              classNameLabel={styles.typeSelect}
              error={t(errors.type?.name || '')}
            />
          </div>

          <div className={cn(styles.group__item, styles.field)}>
            <div className={styles.field__label}>
              {t('date')}
              <span className={styles.required}>*</span>
            </div>
            <DatePicker.RangePicker
              name="dates"
              className="datepicker"
              format="DD.MM.YYYY"
              value={[values.publication_date, values.removal_date]}
              onChange={dates => {
                if (dates) {
                  setFieldValue('publication_date', dates[0]);
                  setFieldValue('removal_date', dates[1]);
                } else {
                  setFieldValue('publication_date', null);
                  setFieldValue('removal_date', null);
                }
              }}
              placeholder={[t('start_date_anquette'), t('end_date_anquette')]}
              disabledDate={(current: Dayjs) =>
                current && current < dayjs().startOf('day')
              }
              onBlur={() => {
                setFieldTouched('publication_date', true);
                setFieldTouched('removal_date', true);
              }}
            />
            <div className={styles.dateErrors}>
              <span className={styles.errorText}>
                {touched.publication_date &&
                  Boolean(errors.publication_date) &&
                  t('date_start_validate')}
              </span>
              <span className={styles.errorText}>
                {touched.publication_date &&
                  Boolean(errors.publication_date) &&
                  t('date_end_validate')}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.selectWrappers}>
          <QuerySelect
            onChange={(value: number[]) => {
              setFieldValue('user_id', value);
              setSelectedUsers(value);
            }}
            value={selectedUsers}
            queryFn={({
              pageParam = 1,
              meta,
            }): Promise<PaginatedResponse<UserItem>> =>
              getUsers({
                sort: '',
                perPage: 100,
                field: '',
                query: meta?.searchQuery as any,
                page: pageParam,
              })
            }
            queryKey={['users', params]}
            star={true}
            label={t('select_recipient')}
            multiple={true}
            getItems={getUserById}
            error={t(errors.user_id as string)}
          />
        </div>

        <div className={styles.selectWrappers}>
          <CustomQuerySelect
            label={t('accommodation_facility')}
            onChange={value => {
              setFieldValue('questionnaire_accommodations', value);
            }}
            value={values.questionnaire_accommodations}
            query="admin/accommodations"
            queryKey={[
              'accommodations-100',
              {
                sort: '',
                perPage: 100,
                field: '',
                page: 1,
              },
            ]}
            params={{
              sort: '',
              perPage: 100,
              field: '',
              page: 1,
            }}
            required
            placeholder={t('to_choose')}
            multiselect
            isSearchEnabled
            error={t(errors.questionnaire_accommodations as string)}
            disabled={values.accommodation_all}
            hasCheckbox
            checkboxLabel={t('all_objects')}
            setIsCheckedAll={value => setFieldValue('accommodation_all', value)}
          />
        </div>

        <div className={styles.selectWrappers}>
          <CustomQuerySelect
            label={t('organizations')}
            query="admin/organization"
            onChange={value => {
              setFieldValue('questionnaire_organizations', value);
            }}
            placeholder={t('validate_organizations')}
            value={values.questionnaire_organizations}
            isSearchEnabled
            queryKey={[
              'organizations',
              {
                sort: '',
                perPage: 100,
                field: '',
                page: 1,
              },
            ]}
            params={{
              sort: '',
              perPage: 100,
              field: '',
              page: 1,
            }}
            required
            multiselect
            disabled={values.organization_all}
            error={t(errors.questionnaire_organizations as string)}
            hasCheckbox
            checkboxLabel={t('all_organizations')}
            setIsCheckedAll={value => setFieldValue('organization_all', value)}
          />
        </div>
      </div>

      <div className={styles.noviceWrapper}>
        <label className={styles.noviceLabel}>
          <CustomCheckbox
            isChecked={values.for_novice}
            onChange={() => setFieldValue('for_novice', !values.for_novice)}
          />
          {t('sending_to_the_first_works')}
        </label>
        <label className={styles.noviceLabel}>
          <CustomCheckbox
            isChecked={values.show_name}
            onChange={() => setFieldValue('show_name', !values.show_name)}
          />
          {t('show_name')}
        </label>
        <label className={styles.noviceLabel}>
          <CustomCheckbox
            isChecked={values.show_phone}
            onChange={() => setFieldValue('show_phone', !values.show_phone)}
          />
          {t('show_phone')}
        </label>
      </div>

      <div className={styles.questionsTableContainer}>
        <QuestionsList
          isSubmitting={isSubmitting}
          questions={questions}
          clickAddQuestion={handleOpenAddQuestionModal}
          clickDeleteQuestion={handleDeleteQuestion}
          clickEditButton={handleOpenEditQuestionModal}
        />
      </div>

      <div className={styles.formControl}>
        <div className={styles.submitContainer}>
          <Button
            type="submit"
            text={t('save_n_exit')}
            disabled={isSubmitting || !isValid}
          />
        </div>
        <div className={styles.submitContainer}>
          <Button
            type="button"
            text={t('anquette_results')}
            disabled
            className={styles.statsButton}
            Icon={StatIcon}
          />
        </div>
      </div>
      <Modal
        isOpen={isAddQuestionModalOpen}
        onClose={handleCloseAddQuestionModal}
        modalStyles={styles.questionModal}
      >
        <CreateQuestionForm
          handleCloseCreate={handleCloseAddQuestionModal}
          clickAddQuestion={handleCreateQuestion}
          questions={questions}
        />
      </Modal>

      <Modal
        isOpen={isEditQuestionModalOpen}
        onClose={handleCloseEditQuestionModal}
        modalStyles={styles.questionModal}
      >
        {editingQuestion && (
          <EditQuestionForm
            questions={questions}
            question={editingQuestion}
            handleCloseCreate={handleCloseEditQuestionModal}
            clickUpdateQuestion={handleUpdateQuestion}
          />
        )}
      </Modal>
    </form>
  );
};

export default QuestionnaireForm;
